import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/commerce-services/commerce-services/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
import GetTenantId from '/src/_includes/authentication/get-tenant-id.md';
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "using-the-storefront-api"
    }}>{`Using the storefront API`}</h1>
    <p>{`Use the Storefront API to retrieve product data from your Commerce catalogs and display it in Commerce frontend experiences. Data includes products, product attribute metadata, prices books, and prices.`}</p>
    <h2 {...{
      "id": "base-url"
    }}>{`Base URL`}</h2>
    <p>{`Send all Storefront API requests to this base URL:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-text"
      }}>{`https://na1-sandbox.api.commerce.adobe.com/<tenantId>/graphql
`}</code></pre>
    <p>{`The URL structure is:`}</p>
    <p><inlineCode parentName="p">{`https://<region>-<environment>.api.commerce.adobe.com/<tenantId>`}</inlineCode></p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`region`}</inlineCode>{` is the cloud region where your instance is deployed.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`environment-type`}</inlineCode>{` is present only for non-production,`}<inlineCode parentName="li">{`sandbox`}</inlineCode>{` environments.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`tenantId`}</inlineCode>{` is the unique identifier for your organization's specific instance within the Adobe Experience Cloud.`}</li>
    </ul>
    <p>{`
`}{` `}{/*Add space between the collapsible section and the previous paragraph*/}</p>
    <details>
      <summary><b>Get your tenant ID</b></summary>

      <GetTenantId mdxType="GetTenantId" />
    </details>
    <h2 {...{
      "id": "authentication"
    }}>{`Authentication`}</h2>
    <p>{`Authentication is not required for the Storefront API.`}</p>
    <h2 {...{
      "id": "headers"
    }}>{`Headers`}</h2>
    <p>{`When making requests to the storefront API, you must include specific HTTP headers to ensure proper authentication and data retrieval. These headers provide necessary information, such as the channel ID, locale, and optional policy and price book headers that tailor the API response to your needs.`}</p>
    <p>{`Include the following headers in GraphQL requests as needed.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Header name`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`AC-View-ID`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Required. The ID for the catalog view that products will be sold through. For example, in the automotive industry, the catalog view could be dealers. In the manufacturing industry, the view could be a manufacturing location for suppliers. You can view the list of available catalog view and associated ids from the `}<a parentName="td" {...{
              "href": "https://experienceleague.adobe.com/en/docs/commerce/optimizer/catalog/channels"
            }}>{`Adobe Commerce Optimizer UI`}</a>{`.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`AC-Policy-{*}`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Optional. The trigger name configured for a policy that sets data access filters to restrict product access based on request attributes and context. Examples include POS physical stores, marketplaces, or advertisement pipelines like Google, Meta, or Instagram. You can view the list of available policies and associated ids from the `}<a parentName="td" {...{
              "href": "https://experienceleague.adobe.com/en/docs/commerce/optimizer/catalog/policies"
            }}>{`Adobe Commerce Optimizer UI`}</a>{`.You can specify multiple policy headers per request. Example: `}<inlineCode parentName="td">{`AC-Policy-Country`}</inlineCode>{`.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`AC-Price-Book-ID`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Optional. Defines how prices are calculated for a specific channel. Use if the merchant uses price books to calculate pricing. Merchandising Services provides a default price book `}<inlineCode parentName="td">{`main`}</inlineCode>{` with currency in US dollars.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`AC-Source-Locale`}</inlineCode>{`:`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Required. The catalog source locale (language or geography) to filter products for display or update, for example `}<inlineCode parentName="td">{`en_US`}</inlineCode>{`. Use the `}<a parentName="td" {...{
              "href": "https://developer-stage.adobe.com/commerce/services/graphql-api/admin-api/index.html#query-channels"
            }}>{`channels query`}</a>{` to retrieve the locale IDs available for each channel.`}</td>
        </tr>
      </tbody>
    </table>
    <h2 {...{
      "id": "request-template"
    }}>{`Request template`}</h2>
    <p>{`Use the following template to submit requests using `}<a parentName="p" {...{
        "href": "https://curl.se/"
      }}>{`curl`}</a>{`. Use required and optional headers as needed. Replace placeholders with required values.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-shell"
      }}>{`curl --request POST \\
-- url https://na1-sandbox.api.commerce.adobe.com/<tenantId>/graphql \\
--header --header 'AC-Environment-ID: <tenantId>' \\
--header 'AC-View-ID:  <channelId>'  \\
--header 'AC-Policy-<POLICY_NAME>: <policyValue>' \\
--header 'AC-Price-Book-ID-<pricebookId>' \\
--header 'AC-Source-Locale: <localeValue>' \\
--data '<apiPayload>'
`}</code></pre>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Placeholder name`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`channelID`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The ID for the channel products will be sold through. Use the `}<a href="https://developer-stage.adobe.com/commerce/services/graphql-api/admin-api/index.html#query-channels">{`channels query`}</a>{` to retrieve available IDs.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`tenantId`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`is the unique identifier for your organization's specific instance within the Adobe Experience Cloud.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`policyName: policyValue`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Optional. The policy trigger name and value that sets data access filters to restrict product access based on request attributes. Use the `}<a href="https://developer-stage.adobe.com/commerce/services/graphql-api/admin-api/index.html#query-policies">{`policies query`}</a>{` to retrieve available policies.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`pricebookID`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Optional. The price book ID used to retrieve the pricing schedule for a SKU.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`localeValue`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The catalog source locale (language or geography) to filter products for display or update.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`apiPayload`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`API payload. See examples in the `}<a href="ccdm-use-cases">{`tutorial.`}</a></td>
        </tr>
      </tbody>
    </table>
    <p>{`For sample requests and examples using the API, see the `}<a parentName="p" {...{
        "href": "api-reference.md"
      }}>{`API Reference`}</a>{` and the `}<a parentName="p" {...{
        "href": "../ccdm-use-case.md"
      }}>{`tutorial`}</a>{`.`}</p>
    <h2 {...{
      "id": "limitations"
    }}>{`Limitations`}</h2>
    <p>{`See `}<a parentName="p" {...{
        "href": "https://experienceleague.adobe.com/en/docs/commerce/optimizer/boundaries-limits"
      }}>{`Limits and boundaries`}</a>{` in the `}<em parentName="p">{`Adobe Commerce Optimizer Guide`}</em>{`.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      